import { useState, FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { createReviewMessageWithHeaderAndFooter } from 'utils/data-transform/review';
import { ProFeedback } from 'modules/marketing-pages/marketing-page.types';
import SeeMoreIconButton from '@z-components/atoms/SeeMoreIconButton';
import ClientReviewCard from '@z-components/molecules/ClientReviewCard';
import Button from '@z-components/atoms/Button';
import EmptyModule from '@z-components/molecules/EmptyModule';

import styles from './ClientReviewCardGroup.module.scss';
import ClientReviewCardNew from '@z-components/molecules/ClientReviewCardNew';

const DISPLAY_COUNT = 3;
const INITIAL_DISPLAY_COUNT = 3;

interface Props {
  centerSeeMore?: boolean;
  initialDisplayCount?: number;
  isReviewsQAEnabled?: boolean;
  reviews: Array<ProFeedback>;
  title?: {
    en: string;
    ja: string;
  };
  variant?: string;
}

const ClientReviewCardGroup: FC<Props> = ({
  centerSeeMore = false,
  initialDisplayCount = INITIAL_DISPLAY_COUNT,
  isReviewsQAEnabled = false,
  reviews = [],
  title,
  variant = '',
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const [displayCount, setDisplayCount] = useState(initialDisplayCount);

  const handleSeeMore = useCallback(() => {
    setDisplayCount(displayCount + DISPLAY_COUNT);
  }, [displayCount]);

  const handleCollapse = useCallback(() => {
    setDisplayCount(DISPLAY_COUNT);
  }, []);

  const readMore =
    reviews.length > displayCount ? (
      <div
        className={classNames(
          styles['see-more'],
          { [styles['see-more-center']]: centerSeeMore },
          { [styles['see-more-swipe']]: variant === 'swipe' }
        )}
      >
        <SeeMoreIconButton onClick={handleSeeMore} testId="pro-profile-review-show-more" />
      </div>
    ) : (
      reviews.length > 0 &&
      variant !== 'swipe' && (
        <div
          className={classNames(styles['see-more'], { [styles['see-more-center']]: centerSeeMore })}
        >
          <img
            className={styles['collapse-icon']}
            src="/static/images/icons/icon-close.svg"
            alt=""
          />
          <Button
            className={classNames('z-btn-blue-text', styles['collapse-btn'])}
            onClick={handleCollapse}
            text={t('close')}
          />
        </div>
      )
    );

  return (
    <article>
      {title && (
        <h3 className={styles['review-list-title']}>{language === 'ja' ? title.ja : title.en}</h3>
      )}
      <div
        className={classNames(styles['pro-reviews'], {
          [styles['pro-reviews-row']]: variant === 'swipe',
        })}
      >
        {reviews.length > 0 ? (
          <section
            className={classNames(styles['review-list'], {
              [styles['review-list-row']]: variant === 'swipe',
            })}
          >
            {reviews
              .sort((reviewA, reviewB) => reviewB.time - reviewA.time)
              .slice(0, displayCount)
              .map((review) => {
                const { rating, time } = review;
                return isReviewsQAEnabled ? (
                  <ClientReviewCardNew
                    key={review.id}
                    inline={false}
                    compact={false}
                    rating={review.rating}
                    time={review.time}
                    title={review.title}
                    ratingPersonality={review.ratingPersonality}
                    ratingQuality={review.ratingQuality}
                    message={review.message}
                    nickname={review.nickname}
                    questionAnswers={review.questionAnswers}
                    age={review.age}
                    pricing={review.pricing}
                    prefecture={review.prefectureName}
                    variant={variant}
                    jobTypeName={{ en: '', ja: review.jobTypeName ?? '' }}
                  />
                ) : (
                  <ClientReviewCard
                    key={review.id}
                    locale={language}
                    message={createReviewMessageWithHeaderAndFooter(review, t)}
                    rating={rating}
                    time={time}
                  />
                );
              })}
            {variant === 'swipe' && <div className={styles['see-more-swipe']}>{readMore}</div>}
          </section>
        ) : (
          <EmptyModule language={language as 'en' | 'ja'} text={t('users:profile.noReview')} />
        )}
      </div>
      {variant !== 'swipe' && readMore}
    </article>
  );
};

export type { Props as ClientReviewCardGroupProps };
export default ClientReviewCardGroup;
